import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader, SectionCard, TWButton, TWDataTable } from '../../../components/ui';
import financeApi from '../../../services/financeApi';
import { useFinanceSection } from '../section/finance-section-context';
import { buildFinanceColumns } from '../columns';
import FinancePageShell from './finance-page-shell';

const PAGE_SIZE = 10;

// What a restaurant was paid before Stripe, kept reachable after the move.
//
// A company on direct charges is no longer billed a weekly statement —
// JobService.CalculateInvoice skips the orders Stripe settled — but the
// statements it was already paid by, and the payouts and fees against them,
// are its own records. The legacy Finance screen that drew them is not the
// screen it lands on any more.
//
// A page of its own rather than a panel folded into Payouts: this runs to
// hundreds of rows with its own paging, and a section that long under a live
// Stripe component reads as the page having two subjects. Payouts links here
// and only when there is something to link to.
//
// Nothing is filtered out, not even the "Invoice for 0 orders" rows a quiet
// week used to produce. It is a ledger; hiding lines from a ledger is how a
// ledger stops being one, and those rows are answered at the source instead —
// the job no longer writes them.
const LegacyTransactions = () => {
  const { companyId } = useFinanceSection();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async (pageNumber) => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    setLoading(true);
    setError('');
    try {
      const res = await financeApi.list(
        `?companyid=${companyId}&pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`,
      );
      if (!mounted.current || id !== requestId.current) return;
      setRows(res?.data?.data || []);
      setTotal(res?.data?.totalRecords || 0);
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      // A refused read is not an empty ledger. Saying so would tell a
      // restaurant its own records are gone.
      setError("We couldn't load your earlier transactions just now.");
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <FinancePageShell>
      <PageHeader
        backTo="/dashboard/finance/payouts"
        backLabel="Payouts"
        title="Earlier payouts"
        description="Statements, payouts and fees from before your payouts moved to your Stripe account."
      />

      <SectionCard>
        {error ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
            <TWButton size="sm" variant="default" onClick={() => load(page)}>
              Try again
            </TWButton>
          </div>
        ) : (
          <TWDataTable
            columns={buildFinanceColumns({ companyId })}
            data={rows}
            rowKey="id"
            loading={loading}
            empty={
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Nothing from before Stripe.
              </span>
            }
            pagination={
              total > PAGE_SIZE
                ? {
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showTotal: (count, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${count} transactions`,
                    onChange: (next) => {
                      setPage(next);
                      load(next);
                    },
                  }
                : undefined
            }
          />
        )}
      </SectionCard>
    </FinancePageShell>
  );
};

export default LegacyTransactions;
